import React, { FC, ReactNode } from 'react'
import { Box, BoxProps } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { Button } from '../Button'

interface IProgressButtonProps {
  children: ReactNode
  onClick?: VoidFunction
  isLoading?: boolean
  isDisabled?: boolean
  percentage?: number
  ariaLabel?: string
}

interface ILoadingProgressIndicatorProps {
  percentage: number
}

const STRIPES_OFFSET_REM = '6rem'

const stripe = keyframes({
  from: { backgroundPosition: '0 0' },
  to: { backgroundPosition: `${STRIPES_OFFSET_REM} 0` },
})

const LoadingProgressIndicator: FC<ILoadingProgressIndicatorProps> = ({
  percentage,
}) => {
  const clipTrailing = Math.round(percentage)

  const commonProps: BoxProps = {
    transition: 'transform 1s ease',
    backgroundPosition: 'center',
    backgroundSize: '100%',
    height: 'full',
    position: 'absolute',
    overflow: 'hidden',
    width: `calc(100% + ${STRIPES_OFFSET_REM})`,
    zIndex: 'hide',
    left: `-${STRIPES_OFFSET_REM}`,
    transform: `translateX(calc(-${100 - clipTrailing
      }% + calc(${STRIPES_OFFSET_REM} * ${1 - clipTrailing / 100})))`,
    borderRadius: '2xl',
  }

  return (
    <>
      <Box sx={commonProps} />
      <Box
        animation={`${stripe} 2s linear infinite`}
        backgroundImage="repeating-linear-gradient(318deg, transparent 0 1rem, white 1rem 2rem)"
        backgroundRepeat="repeat-x"
        opacity={0.1}
        sx={commonProps}
      />
    </>
  )
}

export const ProgressButton: FC<IProgressButtonProps> = ({
  children,
  isDisabled = false,
  isLoading = false,
  percentage = 0,
  onClick,
  ariaLabel,
}) => (
  <Button
    _disabled={{
      opacity: 1,
      pointerEvents: 'none',
    }}
    aria-label={ariaLabel ? ariaLabel : 'in progress'}
    data-testid="progress-button"
    isDisabled={isDisabled || isLoading}
    onClick={onClick}
    overflow="hidden"
    position="relative"
    width="full"
    zIndex="base">
    {/* Chakra-Uis way of displaying a spinner doesn't play well with our custom progress indicator. Same story for `progress`*/}
    {isLoading ? (
      <>
        {children}
        <LoadingProgressIndicator percentage={percentage} />
      </>
    ) : (
      <Box>{children}</Box>
    )}
  </Button>
)
